import { AdviceI18nKeys, AdviceWording, I18nKeys } from '@jump-tech-frontend/app-config';
import { Advice, AdviceSeverity, ClassificationResult, Outcome } from '@jump-tech-frontend/domain';

export type AnalysisStatus =
  | 'success'
  | 'quality_issues'
  | 'low_confidence'
  | 'wrong_equipment'
  | 'no_detection'
  | 'in_progress'
  | 'error';

export type AdviceText = keyof AdviceWording;

type AiValidationI18n = I18nKeys['aiValidation'] | undefined;

const STATUS_BY_OUTCOME = {
  success: 'success',
  qualityIssues: 'quality_issues',
  lowConfidence: 'low_confidence',
  wrongEquipment: 'wrong_equipment',
  noDetection: 'no_detection',
  error: 'error'
} as const satisfies Record<Outcome, AnalysisStatus>;

const FALLBACK_SEVERITY_BY_OUTCOME = {
  success: 'success',
  qualityIssues: 'warning',
  lowConfidence: 'warning',
  wrongEquipment: 'warning',
  noDetection: 'info',
  error: 'error'
} as const satisfies Record<Outcome, AdviceSeverity>;

const DEFAULT_GENERIC_WORDING: AdviceI18nKeys['generic'] = {
  success: 'Your photo looks good.',
  info: 'Please check your photo.',
  warning: 'Your photo may need retaking.',
  error: "We couldn't check your photo."
};

const EQUIPMENT_PARAMS = ['equipmentType', 'expectedEquipment', 'detectedEquipment'];

const UNTRANSLATED_KEY_PREFIX = '_commons.';

export function statusOf(result: ClassificationResult): AnalysisStatus {
  return lookupByOutcome(STATUS_BY_OUTCOME, result.outcome) ?? 'error';
}

export function mismatchOf(result: ClassificationResult): Advice | undefined {
  return result.advice?.find(({ code }) => code === 'equipmentMismatch');
}

export function adviceTexts(result: ClassificationResult, text: AdviceText, i18n: AiValidationI18n): string[] {
  const advice = result.advice ?? [];
  if (advice.length === 0) {
    return [genericWording(fallbackSeverity(result.outcome), i18n)];
  }

  return advice.map(item => adviceText(item, text, i18n));
}

export function interpolate(template: string, params: Readonly<Record<string, string | number>>): string {
  return template.replace(/%(\w+)%/g, (_, key: string) => String(params[key] ?? ''));
}

export function equipmentName(equipment: string, i18n: AiValidationI18n): string {
  const names: Record<string, string | undefined> = {
    FuseCutOut: i18n?.fuseCutout || 'Fuse Cutout',
    ConsumerUnit: i18n?.consumerUnit || 'Consumer Unit',
    IsolatorSwitch: i18n?.isolatorSwitch || 'Isolator Switch'
  };

  return names[equipment] ?? (i18n?.equipment || 'equipment');
}

function adviceText(advice: Advice, text: AdviceText, i18n: AiValidationI18n): string {
  const wording = wordingFor(advice.code, i18n?.advice);
  if (wording === undefined || !isTranslated(wording[text])) {
    return genericWording(advice.severity, i18n);
  }

  return interpolate(wording[text], displayParams(advice.params, i18n));
}

function wordingFor(code: string, adviceKeys: AdviceI18nKeys | undefined): AdviceWording | undefined {
  if (adviceKeys === undefined || code === 'generic') {
    return undefined;
  }

  const entry = Object.entries(adviceKeys).find(([key]) => key === code);
  if (entry === undefined || !isAdviceWording(entry[1])) {
    return undefined;
  }

  return entry[1];
}

function isAdviceWording(value: unknown): value is AdviceWording {
  return (
    typeof value === 'object' &&
    value !== null &&
    'message' in value &&
    typeof value.message === 'string' &&
    'label' in value &&
    typeof value.label === 'string'
  );
}

function genericWording(severity: AdviceSeverity, i18n: AiValidationI18n): string {
  const wording = i18n?.advice?.generic?.[severity];
  if (wording === undefined || !isTranslated(wording)) {
    return DEFAULT_GENERIC_WORDING[severity];
  }

  return wording;
}

function isTranslated(wording: string): boolean {
  return wording.length > 0 && !wording.startsWith(UNTRANSLATED_KEY_PREFIX);
}

function fallbackSeverity(outcome: Outcome | undefined): AdviceSeverity {
  return lookupByOutcome(FALLBACK_SEVERITY_BY_OUTCOME, outcome) ?? 'error';
}

function lookupByOutcome<T>(lookup: Readonly<Record<Outcome, T>>, outcome: string | undefined): T | undefined {
  const entry = Object.entries(lookup).find(([key]) => key === outcome);

  return entry?.[1];
}

function displayParams(params: Advice['params'], i18n: AiValidationI18n): Record<string, string> {
  const display: Record<string, string> = { expectedEquipment: equipmentName('', i18n) };

  for (const [key, value] of Object.entries(params)) {
    display[key] = displayValue(key, value, i18n);
  }

  if (typeof params['megapixels'] === 'number') {
    display['score'] = `${params['megapixels']}MP`;
    display['threshold'] = megapixelThreshold(params['threshold']);
  }

  return display;
}

function displayValue(key: string, value: string | number, i18n: AiValidationI18n): string {
  if (EQUIPMENT_PARAMS.includes(key)) {
    return equipmentName(String(value), i18n);
  }

  if (typeof value !== 'number') {
    return value;
  }

  if (key === 'confidence') {
    return `${value.toFixed(1)}%`;
  }

  if (key === 'score' || key === 'threshold') {
    return `${Math.round(value * 100)}%`;
  }

  return String(value);
}

function megapixelThreshold(threshold: string | number | undefined): string {
  if (threshold === undefined) {
    return '';
  }

  return `${threshold}MP`;
}
